import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Building2, AlertCircle, CheckCircle, Clock, CreditCard, Printer, Bus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TransportFeePayment = () => {
  const [activeTab, setActiveTab] = useState('history');
  const [payments, setPayments] = useState([]);
  const [transportStudents, setTransportStudents] = useState([]); // Students enrolled in transport
  const [mainStudents, setMainStudents] = useState([]); // All students from main system
  const [banks, setBanks] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPayer, setSelectedPayer] = useState(null);
  const [dueReport, setDueReport] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [dueMonths, setDueMonths] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Receipt state
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    payment_number: `TP-${Date.now()}`,
    receipt_number: `RCP-TP-${Date.now()}`,
    payer_type: 'Student',
    student_id: '',
    payer_name: '',
    route_id: '',
    route_name: '',
    payment_date: new Date().toISOString().split('T')[0],
    month: '',
    year: new Date().getFullYear(),
    amount: 0,
    fine_amount: 0,
    fine_reason: '',
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    reference_number: '',
    remarks: '',
    received_by: 'Admin'
  });

  useEffect(() => {
    fetchPayments();
    fetchTransportStudents();
    fetchMainStudents();
    fetchBanks();
    fetchRoutes();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  useEffect(() => {
    if (transportStudents.length > 0) {
      generateDueReport();
    }
  }, [transportStudents, payments]);

  const fetchPayments = async () => {
    try {
      const response = await apiClient.get('/api/transport/fee-payments');
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchTransportStudents = async () => {
    try {
      const response = await apiClient.get('/api/transport/students?status=Active');
      setTransportStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching transport students:', error);
    }
  };

  const fetchMainStudents = async () => {
    try {
      const response = await apiClient.get('/api/students/dropdown');
      setMainStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching main students:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await apiClient.get('/api/transport/routes');
      setRoutes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/accounts/banks');
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  // Calculate due months for a student based on their admission date
  const calculateDueMonths = (student) => {
    const admissionDate = new Date(student.admission_date);
    const today = new Date();
    const dueMonthsList = [];
    
    // Get all paid months for this student
    const paidMonths = payments
      .filter(p => p.student_id === student.student_id)
      .map(p => `${p.month}-${p.year}`);
    
    let current = new Date(admissionDate.getFullYear(), admissionDate.getMonth(), 1);
    while (current <= today) {
      const monthName = current.toLocaleString('default', { month: 'long' });
      const year = current.getFullYear();
      const monthKey = `${monthName}-${year}`;
      
      if (!paidMonths.includes(monthKey)) {
        dueMonthsList.push({
          month: monthName,
          year: year,
          key: monthKey
        });
      }
      current.setMonth(current.getMonth() + 1);
    }
    
    return dueMonthsList;
  };

  // Generate due report
  const generateDueReport = () => {
    const report = [];
    
    // Process transport enrolled students
    transportStudents.forEach(student => {
      // Get student name from main system if available
      const mainStudent = mainStudents.find(s => s.id === student.student_id);
      const studentName = mainStudent?.full_name || student.student_name;
      
      const dues = calculateDueMonths(student);
      const totalDue = dues.length * (student.monthly_fee || 0);
      
      report.push({
        id: student.id,
        payer_id: student.student_id,
        name: studentName,
        admission_number: mainStudent?.admission_number || '',
        route: student.route_name,
        monthly_fee: student.monthly_fee || 0,
        due_months: dues.length,
        total_due: totalDue,
        status: dues.length === 0 ? 'paid' : dues.length > 2 ? 'overdue' : 'due',
        payer_data: student
      });
    });
    
    setDueReport(report);
  };

  const handlePayerTypeChange = (type) => {
    setFormData({
      ...formData,
      payer_type: type,
      student_id: '',
      payer_name: '',
      route_id: '',
      route_name: '',
      amount: 0
    });
    setSelectedPayer(null);
    setSelectedMonths([]);
    setDueMonths([]);
  };

  const handleStudentChange = (studentId) => {
    // Check if student is enrolled in transport
    const transportStudent = transportStudents.find(s => s.student_id === studentId || s.id === studentId);
    
    if (!transportStudent) {
      alert('This student is not enrolled in transport. Please add them first from "Add Student" page.');
      return;
    }
    
    // Get name from main students
    const mainStudent = mainStudents.find(s => s.id === studentId);
    const studentName = mainStudent?.full_name || transportStudent.student_name;
    
    const dues = calculateDueMonths(transportStudent);
    setDueMonths(dues);
    setSelectedPayer({ ...transportStudent, student_name: studentName });
    setFormData({
      ...formData,
      student_id: transportStudent.student_id,
      payer_name: studentName,
      route_id: transportStudent.route_id,
      route_name: transportStudent.route_name,
      amount: 0
    });
    setSelectedMonths([]);
  };

  // Handle multiple month selection
  const handleMonthToggle = (monthKey) => {
    const monthlyFee = selectedPayer?.monthly_fee || 0;
    
    setSelectedMonths(prev => {
      let newSelected;
      if (prev.includes(monthKey)) {
        newSelected = prev.filter(m => m !== monthKey);
      } else {
        newSelected = [...prev, monthKey];
      }
      
      const totalAmount = newSelected.length * monthlyFee;
      setFormData(f => ({
        ...f,
        amount: totalAmount,
        month: newSelected.length === 1 ? newSelected[0].split('-')[0] : `${newSelected.length} months`,
        year: newSelected.length === 1 ? parseInt(newSelected[0].split('-')[1]) : new Date().getFullYear()
      }));
      
      return newSelected;
    });
  };

  const handleSelectAllMonths = () => {
    const monthlyFee = selectedPayer?.monthly_fee || 0;
    const allKeys = dueMonths.map(d => d.key);
    setSelectedMonths(allKeys);
    
    const totalAmount = allKeys.length * monthlyFee;
    setFormData(f => ({
      ...f,
      amount: totalAmount,
      month: `${allKeys.length} months`,
      year: new Date().getFullYear()
    }));
  };

  const handleClearMonths = () => {
    setSelectedMonths([]);
    setFormData(f => ({ ...f, amount: 0, month: '' }));
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setFormData({ ...formData, bank_id: bankId, bank_name: bank.bank_name });
    }
  };

  const isBankPayment = formData.payment_mode !== 'Cash';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedMonths.length === 0) {
      alert('Please select at least one month');
      return;
    }
    
    if (isBankPayment && !formData.bank_id) {
      alert('Please select a bank');
      return;
    }
    
    setLoading(true);
    try {
      const monthlyFee = selectedPayer?.monthly_fee || 0;
      
      // Create separate payment entries for each month
      const paymentPromises = selectedMonths.map(async (monthKey, index) => {
        const [month, year] = monthKey.split('-');
        const payload = {
          ...formData,
          id: `TP-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
          receipt_number: `RCP-TP-${Date.now()}-${index}`,
          month: month,
          year: parseInt(year),
          amount: monthlyFee
        };
        return apiClient.post('/api/transport/fee-payments', payload);
      });
      
      await Promise.all(paymentPromises);
      
      fetchPayments();
      handleCloseModal();
      
      // Show combined receipt
      const monthsList = selectedMonths.map(m => {
        const [month, year] = m.split('-');
        return `${month} ${year}`;
      }).join(', ');
      
      setReceiptData({
        ...formData,
        receipt_number: `RCP-TP-MULTI-${Date.now()}`,
        month: monthsList,
        months_paid: selectedMonths.length,
        amount: formData.amount,
        route_name: selectedPayer?.route_name
      });
      setIsReceiptModalOpen(true);
      
    } catch (error) {
      alert('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPayer(null);
    setSelectedMonths([]);
    setDueMonths([]);
    setFormData({
      payment_number: `TP-${Date.now()}`,
      receipt_number: `RCP-TP-${Date.now()}`,
      payer_type: 'Student',
      student_id: '',
      payer_name: '',
      route_id: '',
      route_name: '',
      payment_date: new Date().toISOString().split('T')[0],
      month: '',
      year: new Date().getFullYear(),
      amount: 0,
      fine_amount: 0,
      fine_reason: '',
      payment_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      reference_number: '',
      remarks: '',
      received_by: 'Admin'
    });
  };

  const openPayModal = (reportItem) => {
    const student = transportStudents.find(s => s.id === reportItem.id);
    if (student) {
      handleStudentChange(student.student_id);
    }
    setIsModalOpen(true);
  };

  // Summary stats
  const totalDue = dueReport.reduce((sum, r) => sum + r.total_due, 0);
  const overdueCount = dueReport.filter(r => r.status === 'overdue').length;
  const paidCount = dueReport.filter(r => r.status === 'paid').length;

  // Get enrolled student IDs for filtering main students dropdown
  const enrolledStudentIds = transportStudents.map(s => s.student_id);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bus className="w-6 h-6 text-blue-600" />
            Transport Fee Management
          </h1>
          <p className="text-gray-500 text-sm">Manage transport fee payments for students</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Collect Fee
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Enrolled Students</p>
            <p className="text-2xl font-bold text-blue-700">{transportStudents.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Total Due</p>
            <p className="text-2xl font-bold text-red-700">₹{totalDue.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-600">Overdue</p>
            <p className="text-2xl font-bold text-yellow-700">{overdueCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Fully Paid</p>
            <p className="text-2xl font-bold text-green-700">{paidCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('history')}
        >
          Payment History
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'due' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('due')}
        >
          Due Report
        </button>
      </div>

      {/* Payment History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardContent className="pt-4">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2">Receipt #</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Student</th>
                  <th className="text-left p-2">Route</th>
                  <th className="text-left p-2">Month</th>
                  <th className="text-right p-2">Fee</th>
                  <th className="text-right p-2">Fine</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-left p-2">Mode</th>
                  <th className="text-center p-2">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan="10" className="text-center p-4 text-gray-500">No payments recorded.</td></tr>
                ) : (
                  payments.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{p.receipt_number || p.payment_number}</td>
                      <td className="p-2">{formatDate(p.payment_date)}</td>
                      <td className="p-2">{p.payer_name}</td>
                      <td className="p-2">{p.route_name}</td>
                      <td className="p-2">{p.month} {p.year}</td>
                      <td className="p-2 text-right">₹{p.amount}</td>
                      <td className="p-2 text-right text-orange-600">{p.fine_amount ? `₹${p.fine_amount}` : '-'}</td>
                      <td className="p-2 text-right font-medium">₹{(p.amount || 0) + (p.fine_amount || 0)}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 text-xs rounded ${p.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {p.payment_mode}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <Button size="sm" variant="outline" onClick={() => { setReceiptData(p); setIsReceiptModalOpen(true); }}>
                          <Printer className="w-3 h-3 mr-1" />Print
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Due Report Tab */}
      {activeTab === 'due' && (
        <Card>
          <CardContent className="pt-4">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2">Student Name</th>
                  <th className="text-left p-2">Admission #</th>
                  <th className="text-left p-2">Route</th>
                  <th className="text-right p-2">Monthly Fee</th>
                  <th className="text-center p-2">Due Months</th>
                  <th className="text-right p-2">Total Due</th>
                  <th className="text-center p-2">Status</th>
                  <th className="text-center p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {dueReport.length === 0 ? (
                  <tr><td colSpan="8" className="text-center p-4 text-gray-500">No students enrolled in transport.</td></tr>
                ) : (
                  dueReport.map((r) => (
                    <tr key={r.id} className={`border-b hover:bg-gray-50 ${r.status === 'overdue' ? 'bg-red-50' : ''}`}>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2">
                        <span className="px-2 py-1 text-xs rounded bg-gray-100">{r.admission_number}</span>
                      </td>
                      <td className="p-2">{r.route}</td>
                      <td className="p-2 text-right">₹{r.monthly_fee}</td>
                      <td className="p-2 text-center">
                        <span className={`font-medium ${r.due_months > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {r.due_months} mo
                        </span>
                      </td>
                      <td className="p-2 text-right font-bold">₹{r.total_due.toLocaleString('en-IN')}</td>
                      <td className="p-2 text-center">
                        {r.status === 'paid' && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 flex items-center justify-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Paid
                          </span>
                        )}
                        {r.status === 'due' && (
                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center gap-1">
                            <Clock className="w-3 h-3" /> Due
                          </span>
                        )}
                        {r.status === 'overdue' && (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 flex items-center justify-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Overdue
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {r.total_due > 0 && (
                          <Button size="sm" onClick={() => openPayModal(r)}>Pay</Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Collect Fee Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Collect Transport Fee</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Student Selection */}
            <div>
              <Label>Select Student *</Label>
              <Select 
                value={formData.student_id} 
                onValueChange={handleStudentChange}
              >
                <SelectTrigger><SelectValue placeholder="-- Select Student --" /></SelectTrigger>
                <SelectContent>
                  {mainStudents.map((s) => {
                    const isEnrolled = enrolledStudentIds.includes(s.id);
                    return (
                      <SelectItem key={s.id} value={s.id} disabled={!isEnrolled}>
                        {s.full_name} ({s.admission_number || s.id.slice(0,8)}) {!isEnrolled && '- Not Enrolled'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Only students enrolled in transport can pay fees.</p>
            </div>

            {/* Payer Info */}
            {selectedPayer && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm space-y-1">
                <p><strong>Route:</strong> {selectedPayer.route_name}</p>
                <p><strong>Monthly Fee:</strong> ₹{selectedPayer.monthly_fee}</p>
                <p><strong>Pickup:</strong> {selectedPayer.pickup_point}</p>
              </div>
            )}

            {/* Month Selection */}
            {selectedPayer && dueMonths.length > 0 && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-red-500 font-medium">{dueMonths.length} month(s) due</span>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={handleSelectAllMonths}>
                      Select All
                    </Button>
                    {selectedMonths.length > 0 && (
                      <Button type="button" size="sm" variant="ghost" onClick={handleClearMonths}>
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                  <div className="grid grid-cols-2 gap-2">
                    {dueMonths.map((d) => (
                      <label 
                        key={d.key} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedMonths.includes(d.key) 
                            ? 'bg-blue-100 border border-blue-300' 
                            : 'bg-white border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedMonths.includes(d.key)}
                          onChange={() => handleMonthToggle(d.key)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{d.month} {d.year}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {selectedMonths.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    ✓ {selectedMonths.length} month(s) selected
                  </p>
                )}
              </div>
            )}

            {selectedPayer && dueMonths.length === 0 && (
              <div className="bg-green-50 p-3 rounded text-green-700 text-sm">
                ✅ All fees paid up to current month!
              </div>
            )}

            {/* Amount Display */}
            {selectedPayer && formData.amount > 0 && (
              <div className="bg-gray-100 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">Amount to Pay:</span>
                    {selectedMonths.length > 1 && (
                      <p className="text-xs text-gray-500">{selectedMonths.length} months × ₹{selectedPayer.monthly_fee}</p>
                    )}
                  </div>
                  <span className="text-2xl font-bold text-green-600">₹{formData.amount?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            {/* Payment Mode */}
            {selectedPayer && selectedMonths.length > 0 && (
              <>
                {/* Fine Section */}
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                  <Label className="text-orange-800 font-medium">Fine (Optional)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Input 
                        type="number" 
                        placeholder="Fine Amount" 
                        value={formData.fine_amount || ''} 
                        onChange={(e) => setFormData({ ...formData, fine_amount: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Input 
                        placeholder="Reason (Late, Damage, etc.)" 
                        value={formData.fine_reason || ''} 
                        onChange={(e) => setFormData({ ...formData, fine_reason: e.target.value })}
                      />
                    </div>
                  </div>
                  {formData.fine_amount > 0 && (
                    <p className="text-xs text-orange-600 mt-1">
                      Total with Fine: ₹{((formData.amount || 0) + (formData.fine_amount || 0)).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Payment Mode *</Label>
                  <Select value={formData.payment_mode} onValueChange={(v) => setFormData({ ...formData, payment_mode: v, bank_id: '', bank_name: '' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Bank">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {isBankPayment && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <Label className="flex items-center gap-2 text-blue-800 mb-2">
                      <Building2 className="w-4 h-4" />Select Bank *
                    </Label>
                    <Select value={formData.bank_id} onValueChange={handleBankChange}>
                      <SelectTrigger><SelectValue placeholder="-- Select Bank --" /></SelectTrigger>
                      <SelectContent>
                        {banks.map((bank) => (
                          <SelectItem key={bank.id} value={bank.id}>
                            {bank.bank_name} (Bal: ₹{bank.current_balance?.toLocaleString('en-IN')})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={loading || !selectedPayer || selectedMonths.length === 0}
              >
                {loading ? 'Processing...' : `Pay ₹${((formData.amount || 0) + (formData.fine_amount || 0)).toLocaleString('en-IN')}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Print Modal */}
      <Dialog open={isReceiptModalOpen} onOpenChange={setIsReceiptModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Transport Fee Receipt
            </DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <div id="receipt-content" className="bg-white p-6 border rounded-lg">
              {/* Receipt Header */}
              <div className="text-center border-b-2 border-gray-800 pb-4 mb-4">
                {collegeInfo?.logo_url && (
                  <img src={collegeInfo.logo_url} alt="Logo" className="h-12 mx-auto mb-2" />
                )}
                <h1 className="text-2xl font-bold text-gray-800">TRANSPORT FEE RECEIPT</h1>
                <p className="text-gray-600 mt-1">{collegeInfo?.college_name || collegeInfo?.name || 'Institution Name'}</p>
                <p className="text-sm text-gray-500">{collegeInfo?.address || ''}</p>
              </div>

              {/* Receipt Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Receipt No:</p>
                  <p className="font-bold text-lg">{receiptData.receipt_number || receiptData.payment_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Date:</p>
                  <p className="font-bold text-lg">{formatDate(receiptData.payment_date)}</p>
                </div>
              </div>

              {/* Payer Info */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Payer Name:</p>
                    <p className="font-semibold">{receiptData.payer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Route:</p>
                    <p className="font-semibold">{receiptData.route_name}</p>
                  </div>
                </div>
              </div>

              {/* Payment Details Table */}
              <table className="w-full border-collapse border border-gray-300 mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left">Description</th>
                    <th className="border border-gray-300 p-2 text-center">Period</th>
                    <th className="border border-gray-300 p-2 text-right">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-2 font-medium">
                      Transport Fee
                      {receiptData.months_paid > 1 && (
                        <span className="text-xs text-gray-500 block">({receiptData.months_paid} months)</span>
                      )}
                    </td>
                    <td className="border border-gray-300 p-2 text-center text-sm">
                      {receiptData.month} {receiptData.year || ''}
                    </td>
                    <td className="border border-gray-300 p-2 text-right font-bold">{receiptData.amount?.toLocaleString('en-IN')}</td>
                  </tr>
                  {receiptData.fine_amount > 0 && (
                    <tr className="bg-orange-50">
                      <td className="border border-gray-300 p-2 font-medium text-orange-700">
                        Fine
                        {receiptData.fine_reason && (
                          <span className="text-xs text-orange-500 block">({receiptData.fine_reason})</span>
                        )}
                      </td>
                      <td className="border border-gray-300 p-2 text-center text-sm">-</td>
                      <td className="border border-gray-300 p-2 text-right font-bold text-orange-700">{receiptData.fine_amount?.toLocaleString('en-IN')}</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-green-50">
                    <td colSpan="2" className="border border-gray-300 p-2 text-right font-bold">Total Paid:</td>
                    <td className="border border-gray-300 p-2 text-right font-bold text-green-700 text-lg">₹{((receiptData.amount || 0) + (receiptData.fine_amount || 0)).toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              </table>

              {/* Payment Mode */}
              <div className="flex justify-between items-center mb-6 p-3 bg-blue-50 rounded">
                <div>
                  <p className="text-sm text-gray-600">Payment Mode:</p>
                  <p className="font-semibold">{receiptData.payment_mode}</p>
                </div>
                {receiptData.bank_name && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Bank:</p>
                    <p className="font-semibold">{receiptData.bank_name}</p>
                  </div>
                )}
              </div>

              {/* Signature Section */}
              <div className="flex justify-between mt-8 pt-4">
                <div className="text-center">
                  <div className="border-t border-gray-400 w-40 pt-2">
                    <p className="text-sm text-gray-600">Payer Signature</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 w-40 pt-2">
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">This is a computer-generated receipt.</p>
                <p className="text-xs text-gray-400 mt-1">Received by: {receiptData.received_by || 'Admin'}</p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsReceiptModalOpen(false)}>Close</Button>
            <Button onClick={() => {
              const printContent = document.getElementById('receipt-content');
              const printWindow = window.open('', '_blank');
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Transport Fee Receipt - ${receiptData?.receipt_number || receiptData?.payment_number}</title>
                    <style>
                      body { font-family: Arial, sans-serif; margin: 20px; }
                      table { width: 100%; border-collapse: collapse; }
                      th, td { border: 1px solid #ccc; padding: 8px; }
                      .text-center { text-align: center; }
                      .text-right { text-align: right; }
                      .font-bold { font-weight: bold; }
                      @media print { body { margin: 0; } }
                    </style>
                  </head>
                  <body>${printContent.innerHTML}</body>
                </html>
              `);
              printWindow.document.close();
              printWindow.print();
            }}>
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportFeePayment;
